/* eslint-disable react-hooks/exhaustive-deps */
import "./ShareResource.scss";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Avatar,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { SyntheticEvent, useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import {
  getActiveUsers,
  getRoleAccessPermissions,
  getUserAccessPermissions,
  share,
} from "../../services/shareResourceService";
import useApi from "../../hooks/useApi";
import { FileActions, FolderFileType } from "../../data/Enums";
import { UserDto } from "../../types/User";
import { OptionDto, RoleDto } from "../../types/Role";
import { getActiveRoles } from "../../services/roleService";
import {
  ShareRequestDto,
  AccessPermissionsDto,
  UserAccessRequestDto,
  RoleAccessRequestDto,
} from "../../types/ShareResource";
import { useUser } from "../../context/UserContext";
import BackdropProgressBar from "../../components/ui/BackdropProgressBar";
import { COLORS, GLOBALSETTINGS, SEVERITIES } from "../../data/constants";
import { useToaster } from "../../hooks/useToaster";
import {
  GLOBALMESSAGES,
  SHARE_FAIL,
  SHARE_SUCCESSFUL,
  SHARERESOURCEVALIDATIONS,
} from "../../constants/messages";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { getDestinationUrl } from "../../utils/commonHelper";
import { logAuditTrace } from "../../services/auditNavigationTraceService";
import { getCurrentESTTime } from "../../utils/dateHelper";

const ROW_HEIGHT = 46;

// Virtualized Listbox with scrollbar fix
const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children) as React.ReactElement[];

    return (
      <div
        ref={ref}
        {...other}
        style={{
          overflow: "hidden",
        }}
      >
        <FixedSizeList
          height={150}
          width="100%"
          itemSize={ROW_HEIGHT}
          itemCount={itemData.length}
          overscanCount={5}
          itemData={itemData}
        >
          {({ index, style }: ListChildComponentProps) => {
            const option = itemData[index];
            return React.cloneElement(option, {
              style: {
                ...style,
                top: (style.top as number) + 0,
              },
            });
          }}
        </FixedSizeList>
      </div>
    );
  },
);

interface ShareResourceProps {
  typeId: number;
  parentFolderId: number | null;
  type: string;
  displayRoles: boolean;
  fileUrl: string | null;
  description: string;
  onClose: () => void;
  canDownload?: boolean;
}

const ShareResource: React.FC<ShareResourceProps> = ({
  typeId,
  parentFolderId,
  type,
  displayRoles,
  fileUrl,
  description,
  onClose,
  canDownload = false, 
}) => {
  const [message, setMessage] = useState("");
  const [validation, setValidation] = useState("");
  const [options, setOptions] = useState<OptionDto[]>([]);
  const [selected, setSelected] = useState<OptionDto[]>([]);
  const [removedUsers, setRemovedUsers] = useState<OptionDto[]>([]);
  const [userPermissions, setUserPermissions] = useState<AccessPermissionsDto[]>([]);
  const [invalidUsers, setInvalidUsers] = useState<string[]>([]);
  const { loading, executeApi } = useApi();
const [shareLink, setShareLink] = useState(false);
const [attachAsCopy, setAttachAsCopy] = useState(false);
  const prevSelectedRef = useRef<OptionDto[]>([]);
  const showToaster = useToaster();
  const { userId, userName } = useUser();
  const hasApiRunRef = useRef<Record<string, boolean>>({});
  useEffect(() => {
    setInvalidUsers([]);
    setValidation("");
    hasApiRunRef.current = {};
  }, []);


useEffect(() => {
  if (canDownload) {
    setAttachAsCopy(false);
    setShareLink(false);
  } else {
    setAttachAsCopy(false);
    setShareLink(true);
  }
}, [canDownload]);


const handleShareLinkChange = (event) => {
  if (!canDownload) return;

  setShareLink(event.target.checked);
};
  const handleAttachAsCopyChange = (event) => setAttachAsCopy(event.target.checked);

  const fetchUsers = async () => {
    const activeUsers = await executeApi<string, UserDto[]>(getActiveUsers, "");
    if (activeUsers) {
      setOptions(
        activeUsers.map((user) => ({
          id: user.userId,
          name: user.userName || user.cachedEmailAddress || "(No Name)",
          email: user.cachedEmailAddress || "",
          phone: user.cachedPhoneNumber || "",
        })),
      );
    }
  };

  const fetchRoles = async () => {
    const activeRoles = await executeApi<string, RoleDto[]>(getActiveRoles, "");
    if (activeRoles) {
      setOptions(
        activeRoles.map((role) => ({
          id: role.roleId,
          name: role.roleName,
          email: "",
          phone: "",
        })),
      );
    }
  };

  useEffect(() => {
    displayRoles ? fetchRoles() : fetchUsers();
  }, []);


  const getAvatarBackgroundColor = (name: string) => {
    const index = name ? name.charCodeAt(0) % COLORS.length : 0;
    return COLORS[index];
  };

  const getNoOptionsText = () =>
    displayRoles
      ? SHARERESOURCEVALIDATIONS.NO_ROLES_FOUND
      : SHARERESOURCEVALIDATIONS.NO_USERS_FOUND;

  const checkUserAccessPermissions = async () => {
    const userAccessRequestDto: UserAccessRequestDto = {
      userIds: selected.map((opt) => opt.id),
      type: FolderFileType[type],
      typeId,
    };
    const permissions = await executeApi<UserAccessRequestDto, AccessPermissionsDto[]>(
      getUserAccessPermissions,
      userAccessRequestDto,
    );
    setUserPermissions(permissions);
    userAccessRequestDto.userIds.forEach((uid) => {
      hasApiRunRef.current[uid] = true;
    });
    return checkInvalidUsers(permissions);
  };

  const checkInvalidUsers = (userPermissions: AccessPermissionsDto[]) => {
    const invalidUserNames = selected
      .filter((sel) => {
        // skip roles whose API hasn't run yet
        if (!hasApiRunRef.current[sel.id]) return false;

        const match = userPermissions && userPermissions.find((p) => p.userId === sel.id);
        return !match || !match.canView;
      })
      .map((sel) => sel.name);

    if (invalidUserNames && invalidUserNames.length > 0) {
      setInvalidUsers(invalidUserNames);
      return false;
    } else {
      setInvalidUsers([]);
      return true;
    }
  };

  const checkRoleAccessPermissions = async () => {
    const roleAccessRequestDto: RoleAccessRequestDto = {
      roleIds: selected.map((opt) => opt.id),
      type: FolderFileType[type],
      typeId,
    };
    const permissions = await executeApi<RoleAccessRequestDto, AccessPermissionsDto[]>(
      getRoleAccessPermissions,
      roleAccessRequestDto,
    );
    setUserPermissions(permissions);
    roleAccessRequestDto.roleIds.forEach((roleId) => {
      hasApiRunRef.current[roleId] = true;
    });
    return checkInvalidRoles(permissions);
  };

  const checkInvalidRoles = (userPermissions: AccessPermissionsDto[]) => {
    const invalidRoles = selected
      .filter((sel) => {
        // skip roles whose API hasn't run yet
        if (!hasApiRunRef.current[sel.id]) return false;

        // check if permissions exist for this role
        const match = userPermissions?.find((p) => p.roleId === sel.id);
        return !match || !match.canView;
      })
      .map((sel) => sel.name);

    if (invalidRoles && invalidRoles.length > 0) {
      setInvalidUsers(invalidRoles);
      return false;
    } else {
      setInvalidUsers([]);
      return true;
    }
  };

  const handleChange = (event: SyntheticEvent, newValue: OptionDto[]) => {
    const removed = prevSelectedRef.current.filter(
      (prev) => !newValue.some((sel) => sel.id === prev.id),
    );
    if (removed.length > 0) setRemovedUsers(removed);
    prevSelectedRef.current = newValue;
    setSelected(newValue);
  };

  useEffect(() => {
    if (userPermissions) {
      displayRoles ? checkInvalidRoles(userPermissions) : checkInvalidUsers(userPermissions);
    }
  }, [removedUsers, attachAsCopy, shareLink]);

  useEffect(() => {
    if (
      selected.length > 0 &&
      (validation === SHARERESOURCEVALIDATIONS.SELECT_USERS ||
        validation === SHARERESOURCEVALIDATIONS.SELECT_ROLES)
    ) {
      setValidation("");
    } else if (
      (shareLink || attachAsCopy) &&
      validation === SHARERESOURCEVALIDATIONS.NO_SELECTION
    ) {
      setValidation("");
    }
  }, [shareLink, attachAsCopy, selected]);

  const handleShare = async () => {
    if (!shareLink && !attachAsCopy) {
      setValidation(SHARERESOURCEVALIDATIONS.NO_SELECTION);
      return;
    }
    if (selected.length === 0) {
      setValidation(
        displayRoles
          ? SHARERESOURCEVALIDATIONS.SELECT_ROLES
          : SHARERESOURCEVALIDATIONS.SELECT_USERS,
      );
      return;
    }

    const valid = displayRoles
      ? await checkRoleAccessPermissions()
      : await checkUserAccessPermissions();
    if (!valid) return;

    let folderId = null;
    let appUrl = getDestinationUrl(parentFolderId);
    const parentId = parentFolderId ?? GLOBALSETTINGS.DEFAULT_STATE;
    if (type === FolderFileType[FolderFileType.Folder]) {
      appUrl += `?folderId=${typeId}&parentFolderId=${parentId}`;
      folderId = typeId;
    } else {
      appUrl += `?fileId=${typeId}&parentFolderId=${parentId}`;
    }
    appUrl += `&${
      displayRoles ? SHARERESOURCEVALIDATIONS.ROLE_PATH : SHARERESOURCEVALIDATIONS.USER_PATH
    }`;

    const emailList = displayRoles
      ? []
      : [...new Set(selected.map((opt) => opt.email?.trim()).filter(Boolean))];
    const roleIds = displayRoles ? selected.map((opt) => opt.id).filter(Boolean) : [];

    const shareRequestDto: ShareRequestDto = {
      emailList,
      phoneList: selected
        .filter((opt) => !opt.email?.trim())
        .map((opt) => opt.phone?.trim())
        .filter(Boolean),
      roleIds,
      attachAsCopy,
      shareLink,
      userName,
      userId,
      fileUrl,
      folderId,
      applicationUrl: appUrl,
      description,
      userMessage: message,
    };

    const success = await executeApi<ShareRequestDto, boolean>(share, shareRequestDto);
    let actionDescription = displayRoles
      ? FileActions.ShareToRole.toString()
      : FileActions.ShareToUser.toString();
    if (!success) {
      actionDescription += GLOBALMESSAGES.FAILED_ADDITION;
    }
    showToaster(
      success ? SHARE_SUCCESSFUL(type, description) : SHARE_FAIL(type, description),
      success ? SEVERITIES.SUCCESS : SEVERITIES.ERROR,
    );
    logAuditTrace({
      userId: userId,
      objectType: type,
      objectId: typeId,
      objectName: description,
      action: actionDescription,
      navigationDateTime: getCurrentESTTime(),
    });
    onClose();
  };


  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" className="top-dialog">
      <DialogTitle className="dialog-title">
        <Avatar src="/folder.svg" alt="file type" className="dialog-avatar" />
        <Tooltip title={description} placement="top-start">
          <span className="dialog-description">Share &quot;{description}&quot;</span>
        </Tooltip>
      </DialogTitle>
      <IconButton aria-label="close" onClick={onClose} className="dialog-close-btn">
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <BackdropProgressBar loading={loading} />
        <Autocomplete
          id="shareData"
          multiple
          options={options}
          value={selected}
          getOptionLabel={(option) => {
            if (!option) return "";
            return option.name || option.email || option.phone || "";
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterSelectedOptions
          loading={loading}
          onChange={handleChange}
          noOptionsText={getNoOptionsText()}
          ListboxComponent={
            ListboxComponent as React.ComponentType<React.HTMLAttributes<HTMLElement>>
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const isInvalid = invalidUsers.includes(option.name);
              const { key, ...rest } = getTagProps({ index });
              return (
                <Chip
                  label={option.name}
                  key={key}
                  {...rest}
                  sx={{
                    backgroundColor: isInvalid ? "rgba(255, 0, 0, 0.1)" : undefined,
                    color: isInvalid ? "red" : undefined,
                    fontWeight: isInvalid ? "bold" : undefined,
                    border: isInvalid ? "1px solid red" : undefined,
                  }}
                  variant="outlined"
                />
              );
            })
          }
          renderOption={(props, option) => (
            <ListItem {...props} sx={{ p: 0 }} key={option.id}>
              <ListItemAvatar>
                <Avatar style={{ backgroundColor: getAvatarBackgroundColor(option.name) }}>
                  {option.name?.[0] || ""}
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={option.name} secondary={option.email} />
            </ListItem>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={displayRoles ? "Select Roles" : "Select Users"}
              variant="outlined"
            />
          )}
        />
        {validation && (
          <Alert severity="error" sx={{ mt: 1 }}>
            <AlertTitle>{validation}</AlertTitle>
          </Alert>
        )}
        {invalidUsers.length > 0 && (
          <Alert severity="error" icon={<ErrorOutlineIcon fontSize="inherit" />} sx={{ mt: 1 }}>
            <AlertTitle>
              {displayRoles
                ? SHARERESOURCEVALIDATIONS.ROLES_NOPERMISSION
                : SHARERESOURCEVALIDATIONS.USERS_NOPERMISSION}
            </AlertTitle>
            {invalidUsers.map((name, index) => (
              <div key={name}>
                {index + 1}. {name}
              </div>
            ))}
          </Alert>
        )}
        <TextField
          id="messageBody"
          multiline
          rows={3}
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          sx={{ mt: 2 }}
          variant="outlined"
          placeholder="Add a message"
        />
<FormControlLabel
  control={
    <Checkbox
      checked={shareLink}
      onChange={handleShareLinkChange}
      color="primary"
      disabled={!canDownload}   
    />
  }
  label="Share link"
/>
{canDownload && (
  <FormControlLabel
    control={
      <Checkbox
        checked={attachAsCopy}
        onChange={handleAttachAsCopyChange}
        color="primary"
      />
    }
    label="Attach as copy"
  />
)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="light">
          Cancel
        </Button>
        <Button
          onClick={handleShare}
          disabled={invalidUsers.length > 0}
          variant="contained"
          color="primary"
        >
          Share
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareResource;
